import { useEffect, useRef, useState } from 'react';
import { lookupGhlWebsiteByDraft } from 'modules/api/digitalServices';

const DEFAULT_POLL_INTERVAL_MS = 2000;
const DEFAULT_MAX_POLL_INTERVAL_MS = 15000;
const DEFAULT_POLL_TIMEOUT_MS = 120000;
const BACKOFF_FACTOR = 1.5;

export type GhlWebsite = {
  ghlBookPageUrl?: string;
  subdomain?: string;
  subdomainFqdn?: string;
  [key: string]: unknown;
};

/**
 * - `loading`     — no answer yet, or an answer is still expected (polling).
 * - `ready`       — DS has the record and its `ghlBookPageUrl`.
 * - `not-found`   — DS has no record for this draft (404): the draft's site
 *                   has not been published yet. Definitive, polling stops.
 * - `unavailable` — polling gave up (timeout, errors, or a record that never
 *                   got a URL) or was never allowed to start.
 */
export type GhlWebsiteStatus = 'loading' | 'ready' | 'not-found' | 'unavailable';

export type GhlWebsiteState = {
  website: GhlWebsite | null;
  status: GhlWebsiteStatus;
};

type LookupResult =
  | { outcome: 'found'; data: GhlWebsite | null }
  | { outcome: 'not-found' }
  | { outcome: 'error'; status?: number };

type Options = {
  // When false, the hook asks DS once but does NOT poll. Callers open the
  // gate once the publish lifecycle has reached the point at which the book
  // builder emits `bba.books.site-ready` (isDigitalBookReady on the View page).
  gateReady?: boolean;
  // True when this session just published the draft. Only then is a 404
  // worth retrying: the DS record is upserted by a Kafka consumer a few
  // seconds after the publish finishes. Otherwise a 404 means "never
  // published" and polling stops at once.
  expectRecord?: boolean;
  // First delay between polls; each next delay grows by BACKOFF_FACTOR.
  pollIntervalMs?: number;
  maxPollIntervalMs?: number;
  // Total time budget for the polls, measured as the sum of the delays.
  pollTimeoutMs?: number;
};

type Settled = {
  draftId: string;
  outcome: 'ready' | 'not-found' | 'other';
};

const hasUrl = (website: GhlWebsite | null | undefined): boolean =>
  Boolean(website?.ghlBookPageUrl);

/**
 * Fetches the `ds__ghl_websites` record of a draft and, while allowed, polls
 * until it carries `ghlBookPageUrl`.
 *
 * DS creates the record only from the book builder's `bba.books.site-ready`
 * Kafka event, emitted after the draft's site is generated on publish. So:
 * - a draft that was never published answers 404 for good — the hook reports
 *   `not-found` and stops;
 * - right after a publish the record lags a few seconds behind — the caller
 *   passes `expectRecord` and the hook polls through the 404s;
 * - every poll is bounded: exponential backoff, a total time budget, and
 *   cancellation on unmount or when the inputs change.
 */
export const useGhlWebsiteState = (
  draftId: string | undefined,
  options: Options = {},
): GhlWebsiteState => {
  const {
    gateReady = true,
    expectRecord = false,
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
    maxPollIntervalMs = DEFAULT_MAX_POLL_INTERVAL_MS,
    pollTimeoutMs = DEFAULT_POLL_TIMEOUT_MS,
  } = options;

  const [state, setState] = useState<GhlWebsiteState & { draftId?: string }>({
    draftId,
    website: null,
    status: 'loading',
  });

  // Last definitive answer per draft, so an effect re-run (the gate opening)
  // does not ask DS again for an answer that cannot change.
  const settledRef = useRef<Settled | null>(null);
  // Lookup in flight, shared across effect re-runs so a gate flip while the
  // first request is pending does not fire a second request.
  const inflightRef = useRef<{ draftId: string; promise: Promise<LookupResult> } | null>(null);

  useEffect(() => {
    if (!draftId) return undefined;

    const settled = settledRef.current?.draftId === draftId ? settledRef.current : null;
    if (settled?.outcome === 'ready') return undefined;
    if (settled?.outcome === 'not-found' && !expectRecord) return undefined;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let nextDelay = pollIntervalMs;
    let waited = 0;

    const lookup = (): Promise<LookupResult> => {
      const inflight = inflightRef.current;
      if (inflight?.draftId === draftId) return inflight.promise;
      const promise = (lookupGhlWebsiteByDraft(draftId) as Promise<LookupResult>).finally(() => {
        if (inflightRef.current?.promise === promise) inflightRef.current = null;
      });
      inflightRef.current = { draftId, promise };
      return promise;
    };

    const keepPolling = (result: LookupResult): boolean => {
      if (!gateReady || waited >= pollTimeoutMs) return false;
      if (result.outcome === 'not-found') return expectRecord;
      return true;
    };

    const attempt = async () => {
      const result = await lookup();
      if (cancelled) return;

      const website = result.outcome === 'found' ? result.data : null;

      if (hasUrl(website)) {
        settledRef.current = { draftId, outcome: 'ready' };
        setState({ draftId, website, status: 'ready' });
        return;
      }

      if (keepPolling(result)) {
        setState((prev) => ({
          draftId,
          website: website ?? (prev.draftId === draftId ? prev.website : null),
          status: 'loading',
        }));
        const delay = Math.min(nextDelay, pollTimeoutMs - waited);
        waited += delay;
        nextDelay = Math.min(nextDelay * BACKOFF_FACTOR, maxPollIntervalMs);
        timer = setTimeout(attempt, delay);
        return;
      }

      const notFound = result.outcome === 'not-found';
      settledRef.current = { draftId, outcome: notFound ? 'not-found' : 'other' };
      setState({
        draftId,
        website,
        status: notFound ? 'not-found' : 'unavailable',
      });
    };

    attempt();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [draftId, gateReady, expectRecord, pollIntervalMs, maxPollIntervalMs, pollTimeoutMs]);

  if (!draftId) return { website: null, status: 'not-found' };
  if (state.draftId !== draftId) return { website: null, status: 'loading' };
  return { website: state.website, status: state.status };
};

/** Returns only the record (null while unknown or absent). */
const useGhlWebsite = (draftId: string | undefined, options: Options = {}): GhlWebsite | null =>
  useGhlWebsiteState(draftId, options).website;

export default useGhlWebsite;
